import { usePublicAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { FC } from "react";
import { Outlet } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useRedirectToLandingPage } from "@/app/router/useRedirectToLandingPage";
import { ReactAriaRouterProvider } from "@/app/router/react-aria-router-provider";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { localStorageModule } from "@/modules/local-storage";
import { useHotkey } from "@/domains/hotkeys/use-hotkey";
import { MDSHotkeyContextProvider, type MDSKeyboardShortcutEvent } from "@/mds/domains/hotkeys";
import { trackKeyboardShortcutPressed } from "@/modules/keyboard-shortcuts/metrics";
import { MDSModalRenderLockProvider } from "@/mds/components/overlays/modal";

interface RootOutletProps {}

const handleMDSKeyboardEvent = ({ shortcut }: MDSKeyboardShortcutEvent) => {
  trackKeyboardShortcutPressed({ shortcut });
};

export const RootOutlet: FC<RootOutletProps> = observer(() => {
  useRedirectToLandingPage();

  const [searchParams, setSearchParams] = useSearchParams();

  const { publicStore } = usePublicAppStore();
  publicStore.useInitializePublicStoreEffects({ searchParams, setSearchParams });
  const hotkeyTarget = typeof window === "undefined" ? null : window;

  useHotkey(
    { key: "|", ctrl: true, meta: true, shift: true },
    () => publicStore.debug.toggleDebugMode(),
    { target: hotkeyTarget, preventDefault: true }
  );

  useEffectOnMount(() => {
    const iosCalendarSetupDone = searchParams.get("ios-calendar-setup");
    if (iosCalendarSetupDone) {
      localStorageModule.writeIosCalendarSetup(true);
    }
    const fromIosApp = searchParams.get("from-ios-app");
    if (fromIosApp) {
      localStorageModule.writeFromIosApp(true);
    }
  });

  return (
    <ReactAriaRouterProvider>
      <MDSHotkeyContextProvider onKeyboardEvent={handleMDSKeyboardEvent}>
        <MDSModalRenderLockProvider>
          <Outlet />
        </MDSModalRenderLockProvider>
      </MDSHotkeyContextProvider>
    </ReactAriaRouterProvider>
  );
});
